import { IonDatetime, IonModal } from '@ionic/react'
import { ComponentProps, useRef } from 'react'
import { Control, Controller, Path } from 'react-hook-form'
import {
  formatDateForUI,
  formatDateForApi,
  fromDatetimeString,
} from '~/shared/lib/date'
import { Button } from '~/shared/ui/Buttons'
import { useModal } from '~/shared/ui/Modal'
import {
  InputWrapper,
  ReadOnlyInput,
  ButtonModalWrapper,
  ModalWrapper,
  TitleModal,
} from './styled'

type DatePickerProps<TFieldValues extends Record<string, string>> = {
  name: Path<TFieldValues>
  control?: Control<TFieldValues>
  label: string
  isMaxCurrentDay?: boolean
  ionDatetimeProps?: ComponentProps<typeof IonDatetime>
}

export function DatePicker<TFieldValues extends Record<string, string>>({
  name,
  control,
  label,
  ionDatetimeProps,
}: DatePickerProps<TFieldValues>) {
  const { openModal, closeModal, isModalShow } = useModal()
  const datetimeRef = useRef<null | HTMLIonDatetimeElement>(null)

  const handleClose = () => {
    closeModal()
  }

  const handleSave = async (controllerOnChange: (date: string) => void) => {
    await datetimeRef?.current?.confirm()
    const rawDate = datetimeRef?.current?.value as string
    const date = formatDateForApi(fromDatetimeString(rawDate))

    controllerOnChange(date)
    closeModal()
  }

  const handleClear = (controllerOnChange: (date: '') => void) => {
    datetimeRef.current?.reset()
    controllerOnChange('')
    closeModal()
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={undefined}
      render={({
        field: { onChange, value },
        fieldState: { error, invalid },
      }) => {
        return (
          <div>
            <InputWrapper onClick={openModal}>
              <ReadOnlyInput
                name={name}
                className={invalid ? 'ion-invalid ion-touched' : ''}
                value={value ? formatDateForUI(value) : value}
                label={label}
                errorText={error?.message || 'Обязательное поле'}
                labelPlacement='floating'
                fill='outline'
                mode='md'
              />
            </InputWrapper>

            <IonModal
              keepContentsMounted={false}
              isOpen={isModalShow}
              onDidDismiss={closeModal}
              data-testid={`date-picker-modal-${name}`}
              style={{
                '--width': 'fit-content',
                '--height': 'fit-content',
                '--border-radius': '20px',
              }}
            >
              <ModalWrapper>
                <TitleModal>{label}</TitleModal>
                <IonDatetime
                  name={name}
                  ref={datetimeRef}
                  value={value || formatDateForApi(new Date())}
                  max={formatDateForApi(new Date())}
                  presentation='date'
                  preferWheel
                  mode='md'
                  {...ionDatetimeProps}
                />
                <ButtonModalWrapper>
                  <Button
                    fullWidth
                    variant='aliceBlueFlooded'
                    onClick={handleClose}
                  >
                    Отменить
                  </Button>
                  <Button
                    fullWidth
                    variant='blueFlooded'
                    onClick={() => handleClear(onChange)}
                  >
                    Очистить
                  </Button>
                  <Button
                    fullWidth
                    variant='limeFlooded'
                    onClick={() => handleSave(onChange)}
                  >
                    Применить
                  </Button>
                </ButtonModalWrapper>
              </ModalWrapper>
            </IonModal>
          </div>
        )
      }}
    />
  )
}
